import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { matchBadge, respBadge, reasonIcon, gbp, pct, dt } from "./crmMatchShared";
import { decisionBadge } from "./crmPropertyShared";

export function CrmInvestorMatches({ investorId }) {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [intros, setIntros] = useState([]);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    const [m, i] = await Promise.all([
      api.get(`/crm/investors/${investorId}/matches`, { params: showAll ? { include_all: true } : {} }),
      api.get(`/crm/investors/${investorId}/introductions`),
    ]);
    setMatches(m.data); setIntros(i.data);
  }, [investorId, showAll]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="inv-matches">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">Matching opportunities</h3>
          <label className="flex items-center gap-1.5 text-xs text-emerald-100/70"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="inv-match-showall" className="accent-brass" /> Show all (not just SOURCE)</label>
        </div>
        <div className="space-y-2">
          {matches.map((m) => (
            <div key={m.property_id} onClick={() => navigate(`/crm/properties/${m.property_id}`)} data-testid={`inv-match-${m.property_id}`} className="bg-[#091E16] rounded-lg border border-white/5 p-3 cursor-pointer hover:bg-white/5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-brass">{m.deal_id}</span>
                <span className="text-white font-medium text-sm">{m.address}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${matchBadge(m.classification)}`}>{m.classification}</span>
                {m.ec_decision && <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${decisionBadge(m.ec_decision)}`}>{m.ec_decision}</span>}
                {m.has_active_intro && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Protected</span>}
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${respBadge(m.distribution_status)}`}>{m.distribution_status}</span>
              </div>
              <div className="text-[11px] text-emerald-100/60 mt-1">{m.area || "—"} · Purchase {gbp(m.proposed_purchase_price)} · GDV {gbp(m.target_gdv)} · Profit {gbp(m.estimated_profit)} · ROI {pct(m.roi)}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">{m.reasons.slice(0, 6).map((r, i) => { const ic = reasonIcon(r.status); return <span key={i} className={`text-[11px] ${ic.c}`}>{ic.m} {r.text}</span>; })}</div>
            </div>
          ))}
          {matches.length === 0 && <p className="text-emerald-100/50 text-sm py-6 text-center">No matching opportunities.</p>}
        </div>
      </div>

      <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="inv-introductions">
        <h3 className="text-sm font-semibold text-white mb-3">Protected introductions</h3>
        {intros.length === 0 ? <p className="text-emerald-100/40 text-xs">No protected introductions.</p> : (
          <table className="w-full text-sm"><thead><tr className="text-left text-xs text-emerald-100/50 border-b border-white/10"><th className="py-2">Property</th><th className="py-2">Introduced</th><th className="py-2">Expires</th><th className="py-2">Status</th></tr></thead>
            <tbody>{intros.map((it) => <tr key={it.id} className="border-b border-white/5"><td className="py-2 text-white">{it.property_label}</td><td className="py-2 text-emerald-100/70">{dt(it.intro_at)}</td><td className="py-2 text-emerald-100/70">{dt(it.protection_expiry)}</td><td className="py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${it.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{it.status}</span></td></tr>)}</tbody>
          </table>
        )}
      </div>
    </>
  );
}
